import getBaseUrl from './baseUrlService'
import { ensureAnalyticsSession } from './analyticsSessionService'
import { getCookie, xsrfTokenKey } from './cookieService'

export const analyticsSessionHeaderName = 'X-AI-Session-Id'

const xsrfType = 0 // 0 = per session, 1 = per request
export const webstorePortalType = 0

export const BaseRoute = '/v2/'
export const BaseApiRoute = '/v2/api/'

export function getApiBaseUrl() {
  return new Promise((resolve, reject) => {
    if (process.env.GATSBY_DEV_OVERRIDE_BACKEND_URL === undefined) {
      getBaseUrl()
        .then((url) => resolve(url))
        .catch((error) => reject(400))
    } else {
      resolve(process.env.GATSBY_DEV_OVERRIDE_BACKEND_URL)
    }
  })
}

// PR #2212 review round 2 (1): `signal` is an optional AbortSignal threaded down to the fetch
// (and the per-session XSRF preflight), so the basket mutation queue can genuinely KILL a
// stalled request on timeout - only an aborted request makes it safe to start the next queued
// mutation; releasing the wait alone would reopen the concurrent-mutation overlap.
export function jsonApiPostRequest(urlPath, bodyObj, isBlob = false, signal = undefined) {
  if (isBlob === false && bodyObj !== null && bodyObj !== undefined) {
    const date = new Date()
    bodyObj = {...bodyObj, timezoneOffsetMinutesToUtc: date.getTimezoneOffset(), portalType: webstorePortalType}
  }

  if (xsrfType === 1) {
    return jsonApiPostRequestWithXSRFPerRequest(urlPath, bodyObj, isBlob)
  } else {
    return jsonApiPostRequestWithXSRFPerSession(urlPath, bodyObj, isBlob, signal)
  }
}

function createDefaultHeaders(xsrf) {
  return withAnalyticsSessionHeader({
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'X-XSRF-TOKEN': xsrf,
  })
}

// The analytics session id is resolved immediately before each request and
// transmitted explicitly — the ai_session cookie alone can miss the first
// requests of a visit and requests from resumed idle tabs (NOR-43). Omitted
// when no id can be resolved.
function withAnalyticsSessionHeader(headers) {
  const sessionId = ensureAnalyticsSession()
  if (sessionId) {
    headers[analyticsSessionHeaderName] = sessionId
  }
  return headers
}

/* Per Request XSRF - Begin */
let getXSRFPerRequestPromise = undefined

function jsonApiPostRequestWithXSRFPerRequest(urlPath, bodyObj, isBlob) {
  return new Promise((resolve, reject) => {
    getApiBaseUrl()
      .then((baseUrl) => {
        getXSRFPerRequest(baseUrl)
          .then((xsrf) => {
            const url = `${baseUrl}${urlPath}`
            fetch(url, {
              credentials: 'include',
              headers: createDefaultHeaders(xsrf),
              method: 'POST',
              body: JSON.stringify(bodyObj),
            })
              .then((response) => {
                switch (response.status) {
                  case 200:
                    // D2-487 review round 1 (3): a 200 whose body fails to parse (a proxy/CDN
                    // handing back HTML) used to leave this promise UNSETTLED forever - json()'s
                    // rejection had no handler, and the outer .catch covers only fetch itself.
                    // For whoAmI that meant queryingUser never resolved and the whoAmIPromise
                    // singleton never cleared for the whole session. Reject with the wrapper's
                    // established { error, requestId } shape so existing catches classify it.
                    if (isBlob === true) {
                      response
                        .blob()
                        .then((blob) => resolve(blob))
                        .catch((error) => reject({ error: error, requestId: bodyObj.requestId }))
                    } else {
                      response
                        .json()
                        .then((data) => resolve(data))
                        .catch((error) => reject({ error: error, requestId: bodyObj.requestId }))
                    }
                    break
                  case 204:
                    resolve(null)
                    break
                  default:
                    response
                      .json()
                      .then((errorData) => {
                        const data = JSON.parse(errorData)
                        reject({ ...data, status: response.status, requestId: bodyObj.requestId })
                      })
                      .catch(() =>
                        reject({ status: response.status, requestId: bodyObj.requestId })
                      )

                    break
                }
              })
              .catch((error) => reject({ error: error, requestId: bodyObj.requestId }))
          })
          .catch((error) => reject({ error: error, requestId: bodyObj.requestId }))
      })
      .catch((error) => reject({ error: error, requestId: bodyObj.requestId }))
  })
}

function getXSRFPerRequest(baseUrl) {
  if (getXSRFPerRequestPromise === undefined) {
    getXSRFPerRequestPromise = new Promise((resolve, reject) => {
      const url = `${baseUrl}${BaseApiRoute}secure`
      fetch(url, { credentials: 'include', headers: withAnalyticsSessionHeader({}) })
        .then((response) => {
          switch (response.status) {
            case 200:
              response
                .json()
                .then((data) => {
                  const xsrf = getCookie(xsrfTokenKey)
                  if (xsrf === undefined) {
                    reject('failed to get xsrf')
                  } else {
                    resolve(xsrf)
                  }
                })
                // PR #2212 review round 1 (2): a 200 whose body is not JSON (a proxy/CDN HTML
                // page) left this promise unsettled FOREVER - and with the per-basket mutation
                // queue, one unsettled call would wedge every later mutation on the basket.
                // The main request path got this exact guard in D2-487 round 1 (3); the XSRF
                // fetchers were missed.
                .catch(() => reject('xsrf response was not json'))
              break
            default:
              reject(response.status)
              break
          }
        })
        .catch((error) => reject(error))
        .finally(() => {
          getXSRFPerRequestPromise = undefined
        })
    })
  }

  return getXSRFPerRequestPromise
}
/* Per Request XSRF - End */

/* Per Session XSRF - Begin */
function jsonApiPostRequestWithXSRFPerSession(urlPath, bodyObj, isBlob, signal) {
  return new Promise((resolve, reject) => {
    jsonApiPostRequestWithSessionXSRF(urlPath, bodyObj, false, isBlob, signal)
      .then((data) => {
        resolve(data)
      })
      .catch((error) => {
        if (error.status === 400 && error.state === undefined) {
          // try again, this time request a new xsrf token
          jsonApiPostRequestWithSessionXSRF(urlPath, bodyObj, true, isBlob, signal)
            .then((data) => resolve(data))
            .catch((error) => reject(error))
        } else {
          reject(error)
        }
      })
  })
}

function jsonApiPostRequestWithSessionXSRF(urlPath, bodyObj, forceNewXSRF, isBlob, signal) {
  return new Promise((resolve, reject) => {
    getApiBaseUrl()
      .then((baseUrl) => {
        getXSRFPerSession(baseUrl, forceNewXSRF, signal)
          .then((xsrf) => {
            const url = `${baseUrl}${urlPath}`
            fetch(url, {
              credentials: 'include',
              headers: createDefaultHeaders(xsrf),
              method: 'POST',
              body: JSON.stringify(bodyObj),
              signal: signal,
            })
              .then((response) => {
                switch (response.status) {
                  case 200:
                    // D2-487 review round 1 (3): a 200 whose body fails to parse (a proxy/CDN
                    // handing back HTML) used to leave this promise UNSETTLED forever - json()'s
                    // rejection had no handler, and the outer .catch covers only fetch itself.
                    // For whoAmI that meant queryingUser never resolved and the whoAmIPromise
                    // singleton never cleared for the whole session. Reject with the wrapper's
                    // established { error, requestId } shape so existing catches classify it.
                    if (isBlob === true) {
                      response
                        .blob()
                        .then((blob) => resolve(blob))
                        .catch((error) => reject({ error: error, requestId: bodyObj.requestId }))
                    } else {
                      response
                        .json()
                        .then((data) => resolve(data))
                        .catch((error) => reject({ error: error, requestId: bodyObj.requestId }))
                    }
                    break
                  case 204:
                    resolve(null)
                    break
                  default:
                    response
                      .json()
                      .then((errorData) => {
                        reject({ ...errorData, status: response.status, requestId: bodyObj.requestId })
                      })
                      .catch((error) => {
                        reject({ status: response.status, requestId: bodyObj.requestId })
                      })

                    break
                }
              })
              .catch((error) => reject({ error: error, requestId: bodyObj.requestId }))
          })
          .catch((error) => reject({ error: error, requestId: bodyObj.requestId }))
      })
      .catch((error) => reject({ error: error, requestId: bodyObj.requestId }))
  })
}

function getXSRFPerSession(baseUrl, forceNewXSRF, signal) {
  return new Promise((resolve, reject) => {
    const url = `${baseUrl}${BaseApiRoute}secure`
    const existingXSRF = getCookie(xsrfTokenKey)

    if (existingXSRF === undefined || forceNewXSRF === true) {
      // The caller's abort signal covers this preflight too - a stalled /secure would
      // otherwise hang the queued mutation past its timeout with nothing left to abort.
      fetch(url, { credentials: 'include', headers: withAnalyticsSessionHeader({}), signal: signal })
        .then((response) => {
          switch (response.status) {
            case 200:
              response
                .json()
                .then((data) => {
                  const xsrf = getCookie(xsrfTokenKey)
                  if (xsrf === undefined) {
                    reject('failed to get xsrf')
                  } else {
                    resolve(xsrf)
                  }
                })
                // PR #2212 review round 1 (2): same unsettled-forever gap as getXSRFPerRequest
                // above - see the comment there.
                .catch(() => reject('xsrf response was not json'))
              break
            default:
              reject(response.status)
              break
          }
        })
        .catch((error) => reject(error))
    } else {
      resolve(existingXSRF)
    }
  })
}
/* Per Session XSRF - End */

export function jsonApiGetRequest(urlPath) {
  return new Promise((resolve, reject) => {
    getApiBaseUrl()
      .then((baseUrl) => {
        const url = `${baseUrl}${urlPath}`
        fetch(url, {
          credentials: 'include',
          headers: withAnalyticsSessionHeader({ Accept: 'application/json' }),
          method: 'GET',
        })
          .then((response) => {
            switch (response.status) {
              case 200:
                response.json().then((data) => resolve(data))
                break
              case 204:
                resolve(null)
                break
              default:
                response
                  .json()
                  .then((errorData) => reject({ ...errorData, status: response.status }))
                  .catch(() => reject({ status: response.status }))
                break
            }
          })
          .catch((error) => reject({ error: error }))
      })
      .catch((error) => reject({ error: error }))
  })
}
